import { PropsWithChildren, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchSession } from "@/lib/session";
import { SessionContext } from "./session-context";
import { getAdminToken, setUnauthorizedHandler } from "@/lib/http";

export function SessionGate({ children }: PropsWithChildren) {
  const location = useLocation();
  const navigate = useNavigate();
  const hasToken = !!getAdminToken();

  // Any 401 from anywhere means the 30-minute token has lapsed. Send the
  // operator to the login screen with the page they were on, and say why —
  // otherwise the portal just appears to have broken.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      navigate("/login", {
        replace: true,
        state: { from: window.location.pathname, expired: true },
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-session"],
    queryFn: fetchSession,
    retry: false,
    enabled: hasToken,
  });

  // No token at all → send to login (preserve return path).
  if (!hasToken) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center p-6 text-sm text-muted-foreground">
        Verifying session…
      </div>
    );
  }

  // Token present but session fetch failed (401/403) → token is invalid or
  // not admin-scoped. Treat as not-authorised.
  if (isError || !data) {
    return <Navigate to="/no-access" replace />;
  }
  if (data.user.user_type !== "internal" || data.scope !== "admin") {
    return <Navigate to="/no-access" replace />;
  }
  return <SessionContext.Provider value={data}>{children}</SessionContext.Provider>;
}
